<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4">
        <div>
          <h1 class="sci-page-title-only">Abastecer Veículo</h1>
          <p class="text-sm text-slate-500 font-medium mt-1">
            {{ showForm ? 'Preencha os dados para concluir o abastecimento' : 'Informe a senha de autorização para iniciar' }}
          </p>
        </div>
      </div>

      <transition name="fade" mode="out-in">
        <!-- VUE: INFORMAR SENHA / LISTA DO DIA -->
        <div v-if="!showForm" key="list" class="space-y-8">
          
          <!-- CARD SENHA -->
          <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-lock-open-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">Autorização</h2>
            </div>
            <div class="p-8">
              <div class="max-w-md mx-auto">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 text-center">Digite a Senha de Autorização:</label>
                <v-text-field
                  v-model="code"
                  @keydown.enter="checkCodeAndOpenForm(code)"
                  :loading="loader"
                  :disabled="loader"
                  placeholder="000 - 000"
                  outlined
                  class="bg-slate-50 rounded-xl font-mono text-center custom-password-input"
                  autofocus
                  hide-details
                />
                <v-btn
                  block
                  depressed
                  large
                  color="primary"
                  class="mt-4 rounded-xl font-bold h-12 shadow-md"
                  @click="checkCodeAndOpenForm(code)"
                  :loading="loader"
                >
                  Verificar Senha
                </v-btn>
              </div>
            </div>
          </div>

          <!-- TABELA DO DIA -->
          <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div class="px-6 py-4 flex items-center" style="background-color: #333333;">
              <div class="flex items-center gap-3">
                <v-icon color="orange" small>mdi-history</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">Abastecimentos do Dia</h2>
              </div>
              <v-spacer></v-spacer>
              <v-btn icon dark small @click="initialize">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>
            <div class="p-6">
              <v-data-table
                :items="current_supplies"
                :headers="headers"
                :loading="loader"
                hide-default-footer
                class="custom-table"
              >
                <template v-slot:item.datehour="{ item }">
                  <span class="text-xs font-semibold text-slate-600">{{ convertDateTime(item.datehour) }}</span>
                </template>
                <template v-slot:item.prefecture_social_name="{ item }">
                  <span class="font-bold text-slate-700">{{ item.prefecture_social_name }}</span>
                </template>
                <template v-slot:item.supply_liters_stocked="{ item }">
                  <span class="font-black text-slate-800">{{ item.supply_liters_stocked ? realMaskII(safeToFixed(item.supply_liters_stocked, 3)) : 0 }} L</span>
                </template>
                <template v-slot:item.supply_bidding_price="{ item }">
                  <span class="font-bold text-slate-600">R$ {{ item.supply_bidding_price ? realMaskII(safeToFixed(item.supply_bidding_price, 3)) : '0,000' }}</span>
                </template>
                <template v-slot:item.total_billing_price="{ item }">
                  <span class="font-black text-slate-800">R$ {{ (item.supply_bidding_price && item.supply_liters_stocked) ? realMask((Number(item.supply_bidding_price) * Number(item.supply_liters_stocked)).toFixed(2)) : '0,00' }}</span>
                </template>
                <template v-slot:item.supply_status="{ item }">
                   <span class="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter bg-slate-100 text-slate-500 border border-slate-200">{{ item.supply_status }}</span>
                </template>
              </v-data-table>
            </div>
          </div>
        </div>

        <!-- FORMULÁRIO DE ABASTECIMENTO -->
        <div v-else key="form" class="space-y-6">
          <v-alert
            v-if="message"
            border="left"
            colored-border
            type="warning"
            elevation="2"
            class="rounded-xl font-bold text-sm"
          >
            {{ message }}
          </v-alert>

          <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-gas-station</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">Dados do Abastecimento</h2>
            </div>

            <div class="p-8">
              <v-form ref="form" v-model="valid" lazy-validation>
                <div class="space-y-8">
                  
                  <!-- SECTION 1: IDENTIFICAÇÃO DO VEÍCULO -->
                  <div class="bg-slate-50 p-6 rounded-xl border border-slate-100">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Prefeitura / Município</label>
                        <v-autocomplete
                          :items="prefectures"
                          item-value="prefecture_id"
                          item-text="prefecture_social_name"
                          v-model="data.prefecture_id"
                          readonly
                          outlined dense
                          class="bg-white rounded-lg shadow-sm font-bold h-[40px]"
                          hide-details="auto"
                          append-icon=""
                        />
                      </div>

                      <div v-if="!data.supply_extra">
                        <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Veículo Selecionado</label>
                        <v-autocomplete
                          :items="vehicles"
                          item-value="vehicle_id"
                          v-model="data.vehicle_id"
                          readonly
                          outlined dense
                          class="bg-white rounded-lg shadow-sm font-bold h-[40px]"
                          hide-details="auto"
                          append-icon=""
                        >
                          <template slot="selection" slot-scope="{ item }">
                            {{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}
                          </template>
                        </v-autocomplete>
                      </div>
                    </div>
                  </div>

                  <v-divider class="opacity-50"></v-divider>

                  <!-- SECTION 2: DADOS OPERACIONAIS -->
                  <div class="space-y-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label class="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Motorista Autorizado</label>
                        <v-autocomplete
                          :items="drivers"
                          item-value="driver_id"
                          item-text="driver_name"
                          v-model="data.driver_id"
                          outlined dense
                          class="bg-white rounded-lg shadow-sm"
                          hide-details="auto"
                          :append-icon="!data.is_autorized ? 'mdi-plus' : ''"
                          @click:append="!data.is_autorized ? (showDriverForm = true) : null"
                          :readonly="data.is_autorized || data.supply_extra_type == 1 || data.supply_extra_type == 2"
                        />
                      </div>
                      
                      <div class="grid grid-cols-2 gap-4">
                        <div>
                          <label class="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">KM Atual</label>
                          <v-text-field
                            v-if="data.is_autorized || data.vehicle_broken_odometer || (!data.vehicle_hodometro && !data.vehicle_horimetro) || data.supply_extra_type == 1 || data.supply_extra_type == 2"
                            v-model="data.supply_km"
                            readonly
                            outlined dense
                            placeholder="000.000"
                            hide-details="auto"
                            class="bg-white rounded-lg font-bold"
                          />
                          <v-text-field
                            v-else
                            v-model="data.supply_km"
                            v-mask-number
                            maxlength="7"
                            outlined dense
                            placeholder="000.000"
                            hide-details="auto"
                            class="bg-white rounded-lg font-bold"
                            @input="handleKmInput"
                          />
                        </div>
                        <div>
                          <label class="block text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2">Nº Cupom Fiscal</label>
                          <v-text-field
                            v-model="data.supply_coupon"
                            :rules="(data.is_autorized && !data.supply_extra) ? [] : fieldRules"
                            :readonly="data.is_autorized && !data.supply_extra"
                            outlined dense
                            hide-details="auto"
                            :placeholder="data.is_autorized && !data.supply_extra ? 'SERÁ PREENCHIDO PELO APP' : 'Nº DO DOCUMENTO'"
                            class="bg-white rounded-lg font-bold border-2 border-slate-200"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <v-divider class="opacity-50"></v-divider>

                  <!-- SECTION 3: DETALHES DO COMBUSTÍVEL E VALORES -->
                  <div class="space-y-6">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                      <div class="md:col-span-1">
                        <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Item / Combustível</label>
                        <v-autocomplete
                          :rules="fieldRules"
                          :items="items"
                          item-value="item_id"
                          item-text="item_name"
                          v-model="data.item_id"
                          :readonly="data.is_autorized && !data.supply_extra"
                          @change="loadBidding(data.item_id)"
                          outlined dense
                          hide-details="auto"
                          class="bg-white rounded-lg shadow-sm"
                        >
                          <template slot="selection" slot-scope="{ item }">
                            <span class="text-sm font-bold">{{ item.item_description_desc }}</span>
                          </template>
                          <template slot="item" slot-scope="{ item }">
                            <span class="text-xs font-medium">{{ item.item_description_desc }} - R$: {{ realMaskII(safeToFixed(item.item_price, 3)) }}</span>
                          </template>
                        </v-autocomplete>
                      </div>

                      <div class="md:col-span-1">
                        <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Preço Licitado</label>
                        <v-text-field
                          v-model="data.supply_bidding_price"
                          v-mask-decimal.br="3"
                          disabled
                          outlined dense
                          hide-details="auto"
                          class="bg-slate-50 rounded-lg font-bold"
                        />
                      </div>

                    </div>

                    <!-- LIMITES E TOTAIS -->
                    <div class="mt-8 flex flex-col md:flex-row gap-6">
                      
                      <!-- Authorized Info (Left when visible) -->
                      <div class="flex-1 space-y-4 bg-blue-50/30 p-6 rounded-2xl border border-blue-100" v-if="data.is_autorized || data.supply_extra">
                          <h4 class="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-2">Limites da Autorização</h4>
                          <div class="grid grid-cols-2 gap-4">
                            <div>
                              <label class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">L. Autoriz.</label>
                              <div class="text-lg font-black text-blue-600 tracking-tighter">{{ data.supply_authorized_liters }} L</div>
                            </div>
                            <div>
                              <label class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">V. Autoriz.</label>
                              <div class="text-lg font-black text-blue-600 tracking-tighter">R$ {{ data.supply_authorized_value }}</div>
                            </div>
                          </div>
                      </div>

                      <!-- Totals (Right/Center) -->
                      <div class="flex-1 grid grid-cols-2 gap-4 bg-slate-50 p-6 rounded-2xl border border-dashed border-slate-200 relative overflow-hidden">
                         <div class="absolute right-0 top-0 h-full w-20 bg-orange-500/5 rotate-12 transform translate-x-10 pointer-events-none"></div>
                         <div>
                           <label class="block text-[10px] font-black text-orange-500 uppercase tracking-widest mb-2">Qtde Abastecida (L)</label>
                           <v-text-field
                             v-if="data.is_autorized && !data.supply_extra"
                             v-model="data.supply_liters_stocked"
                             readonly
                             outlined dense
                             hide-details="auto"
                             class="bg-white rounded-lg font-black text-lg"
                             placeholder="0,000"
                             color="orange"
                           />
                           <v-text-field
                             v-else
                             v-model="data.supply_liters_stocked"
                             v-mask-decimal.br="3"
                             :rules="data.is_autorized ? [] : litrosRules"
                             @input="calculateTotals"
                             outlined dense
                             hide-details="auto"
                             class="bg-white rounded-lg font-black text-lg"
                             placeholder="0,000"
                             color="orange"
                           />
                         </div>
                         <div>
                           <label class="block text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-2">Total Final (R$)</label>
                           <div class="bg-emerald-600 h-[40px] px-4 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-600/20">
                             <span class="text-white font-black text-lg tracking-tight">R$ {{ realMask(safeToFixed(data.supply_value || 0, 2)) }}</span>
                           </div>
                         </div>
                      </div>

                    </div>
                  </div>
                </div>

                <!-- FORM ACTIONS -->
                <div class="flex items-center justify-end gap-3 mt-12 pt-8 border-t border-slate-100">
                  <v-btn
                    depressed
                    large
                    text
                    color="secondary"
                    class="rounded-xl font-bold px-8 h-12"
                    @click="[showForm = false, code = '']"
                  >
                    Cancelar
                  </v-btn>
                  <v-btn
                    v-if="data.is_autorized && !data.supply_extra"
                    depressed
                    large
                    color="success"
                    :disabled="loader"
                    :loading="loader"
                    class="rounded-xl font-black px-12 h-12 shadow-lg shadow-emerald-500/20 uppercase tracking-widest"
                    @click="liberarAbastecimento"
                  >
                    Liberar Abastecimento
                  </v-btn>
                  <v-btn
                    v-else
                    depressed
                    large
                    color="primary"
                    :disabled="!valid || loader"
                    :loading="loader"
                    class="rounded-xl font-black px-12 h-12 shadow-lg shadow-orange-500/20 uppercase tracking-widest"
                    @click="validate"
                  >
                    Concluir Abastecimento
                  </v-btn>
                </div>
              </v-form>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- DIALOG NOVO MOTORISTA -->
    <v-dialog v-model="showDriverForm" max-width="500" persistent>
      <div class="bg-white rounded-2xl overflow-hidden shadow-2xl font-sans">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <h3 class="text-white font-black uppercase tracking-widest text-xs flex items-center gap-2">
            <v-icon color="orange" small>mdi-account-plus</v-icon>
            Cadastrar Novo Motorista
          </h3>
          <button @click="[(showDriverForm = false), (driver_name = '')]" class="text-slate-400 hover:text-white transition-colors">
            <v-icon small color="inherit">mdi-close</v-icon>
          </button>
        </div>
        <div class="p-8">
          <v-form @submit.prevent="newDriver" class="space-y-6">
            <div>
              <label class="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Nome Completo:</label>
              <v-text-field
                :rules="fieldRules"
                v-model="driver_name"
                outlined dense
                placeholder="Ex: João Silva"
                class="bg-slate-50 rounded-xl"
                autofocus
              />
            </div>
            <div class="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <v-btn text color="secondary" @click="[(showDriverForm = false), (driver_name = '')]">Cancelar</v-btn>
              <v-btn
                depressed
                color="primary"
                :disabled="!driver_name"
                :loading="loader"
                class="rounded-lg font-black px-8 h-10 shadow-md"
                @click="newDriver"
              >Confirmar</v-btn>
            </div>
          </v-form>
        </div>
      </div>
    </v-dialog>

    <v-snackbar multi-line :color="snack.color" v-model="snack.show" transition="fade-transition">
      <span class="font-bold text-sm">{{ snack.message }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- DIALOG AGUARDANDO AUTORIZAÇÃO NO APP DO USUÁRIO -->
    <v-dialog v-model="isWaitingUserAppAuth" max-width="420" persistent>
      <div class="bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
        <div class="p-8 text-center space-y-6">
          <div class="flex justify-center">
            <div class="relative flex items-center justify-center">
              <v-progress-circular indeterminate color="orange" size="80" width="6" />
              <v-icon color="orange" size="36" class="absolute">mdi-cellphone-lock</v-icon>
            </div>
          </div>

          <div>
            <h3 class="text-white text-lg font-black uppercase tracking-wide">AGUARDANDO PORTADOR</h3>
            <p class="text-orange-400 text-xs font-bold mt-1 uppercase">
              {{ currentBlackCard ? currentBlackCard.client_name : '' }}
            </p>
            <p class="text-slate-400 text-xs mt-3 leading-relaxed">
              Solicitação enviada ao aplicativo móvel do titular.<br/>
              Aguardando a digitação da senha pelo usuário no celular...
            </p>
          </div>

          <div class="pt-2">
            <v-btn
              block
              outlined
              color="error"
              class="rounded-xl font-bold text-xs uppercase tracking-widest"
              @click="cancelAuthWaiting"
            >
              CANCELAR SOLICITAÇÃO
            </v-btn>
          </div>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
import { io } from "socket.io-client";
export default {
  props: ["id"],
  mixins: [Helpers],
  data: () => ({
    retorno: [],
    valid: false,
    loader: false,
    socket: null,
    code: "",
    showForm: false,
    showDriverForm: false,
    total: 0,
    current_supplies: [],
    prefectures: [],
    items: [],
    vehicles: [],
    drivers: [],
    driver_name: "",
    message: "",
    headers: [
      { text: "Data/Hora", value: "datehour" },
      { text: "Prefeitura", value: "prefecture_social_name" },
      { text: "Litros Abastecidos", value: "supply_liters_stocked" },
      { text: "Preço Licitado", value: "supply_bidding_price" },
      { text: "T. Licitado", value: "total_billing_price" },
      { text: "Status", value: "supply_status" },
      { text: "Número do Cupom", value: "supply_coupon" },
    ],
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    litrosRules: [
      (v) => !!v || "Este campo é requerido",
      (v) => Number(v.replace(".", "").replace(",", ".")) > 0 || "Este campo é requerido",
    ],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      supply_id: null,
      supply_liters_stocked: "0.000",
      supply_authorized_liters: "0.000",
      supply_value: "0.00",
      supply_extra: false,
      supply_bidding_price: "0",
      supply_authorized_value: "0.00",
      supply_adjusted: false,
      supply_billed: false,
      supply_status: "CONCLUIDO",
      item_id: null,
      vehicle_id: null,
      provider_id: null,
      prefecture_id: null,
      is_autorized: false,
      driver_id: null,
      vehicle_hodometro: true,
      vehicle_horimetro: true,
      vehicle_broken_odometer: false,
      supply_extra_type: null,
    },
    // Black Card State
    isBlackCardDialog: false,
    isWaitingUserAppAuth: false,
    pendingAuthRequest: null,
    blackCardPin: "",
    currentBlackCard: null,
    cardLimitType: "VALUE",
  }),
  beforeCreate() {
    if (!sessionStorage.getItem("token") && !localStorage.getItem("token")) {
      this.$router.push("/login");
    }
  },
  created() {
    this.Helpers;
    this.$http.get(`/prefectures`).then((pf) => (this.prefectures = pf.data));
    this.$http.get(`/vehicles`).then((v) => (this.vehicles = v.data));
    this.initialize();
  },
  mounted() {
    this.setupSocket();
  },
  methods: {
    setupSocket() {
      if (this.$socket) {
        this.socket = this.$socket;
      } else {
        const socketUrl = process.env.NODE_ENV === "production"
          ? "https://api.sigfrotagov.com.br"
          : (this.$http.defaults.baseURL || "").replace('/api', '').replace('/v1', '').replace(/\/$/, "");
        this.socket = io(socketUrl || undefined);
      }
      
      this.socket.on("connect", () => {
        console.log("Connected to WebSocket");
      });

      this.socket.on("supply_updated", () => {
        console.log("Supply updated event received, refreshing list...");
        this.initialize();
      });

      this.socket.on("disconnect", () => {
        console.log("Disconnected from WebSocket");
      });
    },
    initialize() {
      this.loader = true;
      this.current_supplies = [];
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/supplies/attendants/${this.$store.getters.getUserData.table_id}`)
        .then((r) => {
          this.current_supplies = r.data;
          this.loader = false;
          this.snack.color = "success";
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    newDriver() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .post("/supplies/drivers", {
          driver_name: this.driver_name,
          prefecture_id: this.data.prefecture_id,
        })
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          this.$http
            .get(`/drivers/prefectures/${this.data.prefecture_id}`)
            .then((d) => {
              this.drivers = d.data;
              const driverItems = d.data.filter((f) =>
                f.driver_name === this.driver_name ? f : null
              );
              this.data.driver_id = driverItems[0].driver_id;
              this.showDriverForm = false;
            });
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = e.message;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    async checkCodeAndOpenForm(code) {
      this.resetData();
      try {
        const res = await this.$http.get(`/supplies/single/code/${code}`);
        if (res.data && res.data.length > 0) {
          if (res.data[0].isBlackCard) {
            const card = res.data[0];
            this.currentBlackCard = card;

            // Solicitar autorização ao App do usuário
            const reqRes = await this.$http.post("/supplies/black-card/request-auth", {
              card_number: card.card_number
            });

            if (reqRes.data.error) {
              this.snack.show = true;
              this.snack.color = "error";
              this.snack.message = reqRes.data.error;
              this.loader = false;
              return;
            }

            const requestData = reqRes.data.request;
            this.pendingAuthRequest = requestData;
            this.isWaitingUserAppAuth = true;
            this.loader = false;

            // Ouvir resposta via WebSocket
            if (this.socket) {
              const eventName = `black_card_authorized_${requestData.requestId}`;
              this.socket.off(eventName); // Limpar anterior se houver
              this.socket.on(eventName, async (authResult) => {
                this.socket.off(eventName);
                if (this.authCheckInterval) clearInterval(this.authCheckInterval);
                if (authResult.success) {
                  this.isWaitingUserAppAuth = false;
                  this.openFormForBlackCard(authResult.card || card);
                } else {
                  this.isWaitingUserAppAuth = false;
                  this.snack.show = true;
                  this.snack.color = "error";
                  this.snack.message = authResult.error || "Autorização recusada no aplicativo.";
                }
              });
            }

            // Polling de segurança via HTTP a cada 2 segundos caso o WebSocket falhe/atrase em produção
            if (this.authCheckInterval) clearInterval(this.authCheckInterval);
            this.authCheckInterval = setInterval(async () => {
              if (!this.isWaitingUserAppAuth) {
                clearInterval(this.authCheckInterval);
                return;
              }
              try {
                const checkRes = await this.$http.get(`/supplies/black-card/pending-request/${card.card_id}`);
                if (checkRes.data && checkRes.data.request) {
                  if (checkRes.data.request.status === 'AUTHORIZED') {
                    clearInterval(this.authCheckInterval);
                    this.isWaitingUserAppAuth = false;
                    this.openFormForBlackCard(card);
                  } else if (checkRes.data.request.status === 'REJECTED') {
                    clearInterval(this.authCheckInterval);
                    this.isWaitingUserAppAuth = false;
                    this.snack.show = true;
                    this.snack.color = "error";
                    this.snack.message = "Solicitação recusada pelo portador.";
                  }
                }
              } catch (err) {
                console.warn("Polling check error:", err);
              }
            }, 2000);

            return;
          }
          const supply = res.data[0] || {};
          this.data.supply_extra = supply.supply_extra;
          this.data.supply_extra_type = supply.supply_extra_type;
          this.data.prefecture_id = supply.prefecture_id;
          this.data.is_autorized = supply.is_autorized;
          this.data.supply_km = supply.supply_km;
          this.data.vehicle_hodometro = !!supply.vehicle_hodometro;
          this.data.vehicle_horimetro = !!supply.vehicle_horimetro;
          
          this.data.vehicle_broken_odometer = !!supply.vehicle_broken_odometer;
          
          if (this.data.vehicle_broken_odometer || (!this.data.vehicle_hodometro && !this.data.vehicle_horimetro)) {
            this.data.supply_km = 0;
          }
          
          this.data.item_billing_price = this.safeToFixed(supply.item_billing_price, 3);
          await this.$http
            .get(`/attendants/${this.$store.getters.getUserData.table_id}`)
            .then((r) => {
              this.$http.get(`/providers/${r.data[0].provider_id}`).then(() => {
                this.$http
                  .get(
                    `/supplies/providers/prefectures/${r.data[0].provider_id}/${this.data.prefecture_id}`
                  )
                  .then((i) => {
                    this.items = i.data.filter((f) => {
                      if (f.item_id == supply.item_id) {
                        return f;
                      }
                    });
                    if (!this.items.length) {
                      this.snack.show = true;
                      this.snack.color = "warning";
                      this.snack.message = "Não foi possível carregar o combustível vinculado a esta autorização.";
                      this.loader = false;
                      this.showForm = false;
                      return;
                    }
                    this.data.item_id = this.items[0].item_id;
                    this.loadBidding(this.items[0].item_id);
                  });
                this.$http
                  .get(`/drivers/prefectures/${this.data.prefecture_id}`)
                  .then((d) => {
                    this.drivers = d.data;
                    this.data.driver_id = supply.driver_id;
                  });
                this.data.provider_id = r.data[0].provider_id;
                this.data.vehicle_id = supply.vehicle_id;
                this.data.supply_authorized_liters =
                  this.safeToFixed(supply.supply_authorized_liters, 3);
                this.data.supply_authorized_value =
                  this.safeToFixed(supply.supply_authorized_value, 2);
                this.data.supply_id = supply.supply_id;
                this.data.supply_liters_stocked = supply.supply_liters_stocked || '0.000';
                this.data.supply_value = supply.supply_value || 0;
                this.data.supply_coupon = supply.supply_coupon || '';
                this.data.supply_km = supply.supply_km || supply.last_km || '';

                if (this.data.supply_extra) {
                  this.data.is_autorized = true;

                  if (this.data.supply_authorized_liters > 0) {
                    this.message = `ATENÇÃO ABASTECIMENTO EXTRA LIMITADO A ${this.realMaskII(
                      this.data.supply_authorized_liters
                    )} LITROS.`;
                  } else if (this.data.supply_authorized_value > 0) {
                    this.message = `ATENÇÃO ABASTECIMENTO EXTRA LIMITADO A R$ ${this.realMask(
                      this.safeToFixed(this.data.supply_authorized_value, 2)
                    )}.`;
                  } else {
                    this.message = null;
                  }
                } else {
                  if (this.data.supply_authorized_liters > 0) {
                    this.message = `ATENCÃO abastecimento com limite de ${this.realMaskII(
                      this.data.supply_authorized_liters
                        ? this.data.supply_authorized_liters
                        : 0
                    )} Litros apenas.`;
                  } else if (this.data.supply_authorized_value > 0) {
                    this.message = `ATENCÃO abastecimento com limite de ${this.realMask(
                      this.data.supply_authorized_value
                        ? this.safeToFixed(this.data.supply_authorized_value, 2)
                        : 0
                    )} Reais apenas.`;
                  } else {
                    this.message = null;
                  }
                }
              });
              this.showForm = true;
            });
        } else {
          const res = await this.$http.get(`/vehicles/code/${code}`);
          if (res.data && res.data.length > 0) {
            if (res.data[0].abastecimento_autorizacao) {
              this.snack.show = true;
              this.snack.color = "warning";
              this.snack.message = "O abastecimento deste veículo somente é permitido mediante autorização.";
              this.loader = false;
              this.showForm = false;
            } else {
              await this.$http
                .get(`/attendants/${this.$store.getters.getUserData.table_id}`)
                .then(async (r) => {
                  if (!r.data || r.data.length === 0) {
                    this.snack.show = true;
                    this.snack.color = "warning";
                    this.snack.message = "Frentista não encontrado no sistema.";
                    this.loader = false;
                    this.showForm = false;
                    return;
                  }
                  this.data.vehicle_id = res.data[0].vehicle_id;
                  await this.$http
                    .get(`/vehicles/${res.data[0].vehicle_id}`)
                    .then(async (veh) => {
                      if (veh.data && veh.data.length > 0) {
                        this.data.prefecture_id = veh.data[0].prefecture_id;
                        this.data.vehicle_hodometro = !!veh.data[0].vehicle_hodometro;
                        this.data.vehicle_horimetro = !!veh.data[0].vehicle_horimetro;
                        
                        this.data.vehicle_broken_odometer = !!veh.data[0].vehicle_broken_odometer;
                        
                        if (this.data.vehicle_broken_odometer || (!this.data.vehicle_hodometro && !this.data.vehicle_horimetro)) {
                          this.data.supply_km = 0;
                        }

                        this.$http
                          .get(`/drivers/prefectures/${veh.data[0].prefecture_id}`)
                          .then((d) => (this.drivers = d.data));
                      }
                    });

                  const providerId = r.data[0].provider_id;
                  if (!providerId) {
                    this.snack.show = true;
                    this.snack.color = "warning";
                    this.snack.message = "Frentista não está vinculado a um fornecedor!";
                    this.loader = false;
                    this.showForm = false;
                    return;
                  }

                  const retornoProvider = await this.$http.get(
                    `/providers/checkItems/${providerId}/${this.data.prefecture_id}`
                  );

                  if (retornoProvider.data.length == 0) {
                    this.snack.show = true;
                    this.snack.color = "warning";
                    this.snack.message = "Nenhum combustível vinculado a esta prefeitura no fornecedor!";
                    this.loader = false;
                    this.showForm = false;
                    return false;
                  } else {
                    this.$http
                      .get(`/providers/${providerId}`)
                      .then((p) => {
                        if (!p.data || p.data.length === 0) {
                          this.snack.show = true;
                          this.snack.color = "warning";
                          this.snack.message = "Fornecedor não encontrado!";
                          this.loader = false;
                          this.showForm = false;
                          return;
                        }
                        this.$http
                          .get(
                            `/supplies/providers/${providerId}/${res.data[0].vehicle_id}/${this.data.prefecture_id}`
                          )
                          .then((i) => {
                            if (!i.data || i.data.length === 0) {
                              this.snack.show = true;
                              this.snack.color = "warning";
                              this.snack.message = "Nenhum contrato ativo/combustível licitado encontrado para este veículo!";
                              this.loader = false;
                              this.showForm = false;
                              return;
                            }
                            this.items = i.data;
                            this.data.item_id = i.data[0].item_id;
                            this.loadBidding(i.data[0].item_id);
                            this.data.prefecture_id = res.data[0].prefecture_id;
                            this.data.item_billing_price =
                                  this.safeToFixed(i.data[0].item_billing_price, 3);
                            
                            this.data.provider_id = providerId;
                            this.data.supply_extra = false;
                            this.data.is_autorized = false;
                            this.data.supply_authorized_liters = 0;
                            this.data.supply_authorized_value = 0;
                            this.showForm = true;
                          });
                      });
                  }
                });
            }
          } else {
            this.snack.show = true;
            this.snack.color = "warning";
            this.snack.message =
              "SENHA INEXISTENTE OU INATIVA. ENTRE EM CONTATO COM O SUPORTE.      " +
              "Possiveis causas: " +
              "Veículo Inativo, Código inválido, Código já utilizado anteriormente.";
            this.loader = false;
            this.showForm = false;
          }
        }
      } catch (error) {
        this.snack.show = true;
        this.snack.color = "error";
        this.snack.message = "Erro ao processar requisição. Verifique se a autorização foi carregada corretamente.";
        this.loader = false;
        this.showForm = false;
      }
    },
    loadBidding(item_id) {
      const dataItem = [...this.items];
      const selectedItem = dataItem.find((v) => v.item_id === item_id) || {};
      this.data.supply_bidding_price = this.safeToFixed(selectedItem.item_billing_price, 3);
      this.calculateTotals();
    },
    calculateTotals() {
      let price = 0;
      const priceDay = this.convertStringNumber(this.data.supply_price_day);
      
      if (priceDay > 0) {
        price = priceDay;
      } else if (this.data.supply_bidding_price) {
        price = this.convertStringNumber(this.data.supply_bidding_price);
      }

      if (price > 0 && this.data.supply_liters_stocked) {
        let liters = this.convertStringNumber(this.data.supply_liters_stocked);
        this.data.supply_value = this.safeToFixed(price * liters, 2);
      } else {
        this.data.supply_value = this.safeToFixed(0, 2);
      }
    },
    safeToFixed(value, digits = 2) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return Number(0).toFixed(digits);
      }
      return numericValue.toFixed(digits);
    },
    checkLitersAndValuesAutirized() {
      if (this.data.message) {
        if (this.data.supply_authorized_liters) {
          if (
            this.data.supply_authorized_liters ==
            this.data.supply_liters_stocked
          ) {
            return true;
          } else {
            return false;
          }
        } else if (this.data.supply_authorized_value) {
          if (this.data.supply_authorized_value == this.data.supply_value) {
            return true;
          } else {
            return false;
          }
        }
      }
    },
    handleKmInput(val) {
      if (val === null || val === undefined || val === '') return;
      const digitsOnly = String(val).replace(/\D/g, '');
      if (digitsOnly.length > 6) {
        const clampedDigits = digitsOnly.slice(0, 6);
        this.data.supply_km = parseInt(clampedDigits, 10);
      } else {
        const num = parseInt(digitsOnly, 10);
        if (!isNaN(num) && num > 999999) {
          this.data.supply_km = 999999;
        }
      }
    },
    validate() {
      this.snack.show = true;
      this.loader = true;
      if (this.$refs.form.validate()) {
        const kmVal = Number(String(this.data.supply_km || 0).replace(/\./g, '').replace(',', '.'));
        if (kmVal > 999999) {
          this.snack.show = true;
          this.snack.color = "warning";
          this.snack.message = "Atenção: O KM informado não pode ultrapassar 999.999!";
          this.loader = false;
          return;
        }
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message =
          "Atencão um ou mais campos precisam ser preenchidos!";
        this.loader = false;
      }
    },
    createOrUpdate() {
      this.loader = true;
      delete this.data.item_billing_price;
      delete this.data.datehour;
      this.$store.dispatch("setLoader", this.loader);
      
      // Se tiver card_id, é SEMPRE Cartão Black → ir direto para o fluxo extra
      if (this.data.card_id) {
        this.concluirAbastecimento();
        return;
      }

      if (this.data.is_autorized) {
        if (
          parseFloat(
            this.convertDecimalNumberII(this.data.supply_authorized_liters)
          ) &&
          parseFloat(
            this.convertDecimalNumberII(this.data.supply_authorized_liters)
          ) <
            parseFloat(
              this.convertDecimalNumberII(this.data.supply_liters_stocked)
            )
        ) {
          this.snack.show = true;
          this.snack.message =
            "O valor de litros ultrapassou o autorizado!".toUpperCase();
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        } else if (
          parseFloat(
            this.convertDecimalNumberII(this.data.supply_authorized_value)
          ) &&
          parseFloat(
            this.convertDecimalNumberII(this.data.supply_authorized_value)
          ) < parseFloat(this.convertDecimalNumberII(this.data.supply_value))
        ) {
          this.snack.show = true;
          this.snack.message =
            "O valor ultrapassou o autorizado!".toUpperCase();
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        } else {
          this.data.supply_status = "CONCLUIDO";
          if (this.data.supply_bidding_price && !this.data.supply_price_day) {
            this.data.supply_price_day = this.data.supply_bidding_price;
          }
          this.$http
            .put(`/supplies/single/app`, this.data)
            .then((r) => {
              this.snack.show = true;
              this.snack.message = r.data.error ? r.data.error : "Abastecimento concluído com sucesso!";
              this.snack.color = `${r.data.error ? "warning" : "success"}`;
              this.loader = false;
              this.$store.dispatch("setLoader", this.loader);
              if (r.data.error == null) {
                setTimeout(() => {
                  this.data = {
                    supply_id: null,
                    supply_price_day: 0,
                    supply_adjusted: false,
                    supply_liters_stocked: false,
                    item_id: null,
                    vehicle_id: null,
                    supply_status: "CONCLUIDO",
                    supply_extra: false,
                    supply_authorized_value: 0,
                    supply_billed: false,
                  };
                  this.data.supply_bidding_price = 0;
                  this.showForm = false;
                  this.code = '';
                  this.initialize();
                }, 1500);
              }
            })
            .catch((e) => {
              this.snack.show = true;
              this.snack.message = e.response?.data?.error || e.message;
              this.snack.color = "error";
              this.loader = false;
              this.$store.dispatch("setLoader", false);
            });
        }
      } else if (!this.data.supply_extra) {
        this.$http
          .post(`/supplies/vehicle/check`, this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.error ? r.data.error : r.data.success;
            this.snack.color = `${r.data.error ? "warning" : "success"}`;
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
            if (r.data.success) {
              setTimeout(() => {
                this.data = {
                  supply_id: null,
                  supply_price_day: 0,
                  supply_adjusted: false,
                  supply_liters_stocked: false,
                  item_id: null,
                  vehicle_id: null,
                  supply_status: "CONCLUIDO",
                  supply_extra: false,
                  supply_authorized_value: 0,
                  supply_billed: false,
                };
                this.data.supply_bidding_price = 0;
                this.showForm = false;
                this.code = "";
                this.initialize();
              }, 1500);
            }
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.response?.data?.error || e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        if (
          parseFloat(
            this.convertDecimalNumberII(this.data.supply_authorized_liters)
          ) &&
          parseFloat(
            this.convertDecimalNumberII(this.data.supply_authorized_liters)
          ) <
            parseFloat(
              this.convertDecimalNumberII(this.data.supply_liters_stocked)
            )
        ) {
          this.snack.show = true;
          this.snack.message =
            "O valor de litros ultrapassou o autorizado!".toUpperCase();
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        } else if (
          parseFloat(
            this.convertDecimalNumberII(this.data.supply_authorized_value)
          ) &&
          parseFloat(
            this.convertDecimalNumberII(this.data.supply_authorized_value)
          ) < parseFloat(this.convertDecimalNumberII(this.data.supply_value))
        ) {
          this.snack.show = true;
          this.snack.message =
            "O valor ultrapassou o autorizado!".toUpperCase();
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        } else {
          console.log("Enviando abastecimento:", JSON.stringify(this.data, null, 2));
          
          const request = this.data.supply_id 
            ? this.$http.put(`/supplies/extra/${this.data.supply_id}`, this.data)
            : this.$http.post(`/supplies/extra`, this.data);
            
          request.then((r) => {
              console.log("Resposta da API:", r.data);
              this.snack.show = true;
              if (r.data.error) {
                  this.snack.message = `Erro: ${r.data.error}`;
                  this.snack.color = "error";
              } else {
                  this.snack.message = r.data.result || "Abastecimento concluído!";
                  this.snack.color = "success";
              }
              this.loader = false;
              this.$store.dispatch("setLoader", this.loader);
              if (r.data.result) {
                setTimeout(() => {
                  this.data = {
                    supply_id: null,
                    supply_price_day: 0,
                    supply_adjusted: false,
                    supply_liters_stocked: false,
                    item_id: null,
                    vehicle_id: null,
                  };
                  this.data.supply_bidding_price = 0;
                  this.showForm = false;
                  this.code = "";
                  this.initialize();
                }, 1500);
              }
            })
            .catch((e) => {
              this.snack.show = true;
              this.snack.message = e.response?.data?.error || e.message;
              this.snack.color = "error";
              this.loader = false;
              this.$store.dispatch("setLoader", false);
            });
        }
      }
    },
    concluirAbastecimento() {
      // Fluxo exclusivo para Cartão Black e Extras
      console.log("Concluindo Cartão Black. Payload:", JSON.stringify(this.data, null, 2));

      if (this.data.card_id) {
        if (this.cardLimitType === 'LITERS') {
          const valLiters = this.convertStringNumber(this.data.supply_liters_stocked);
          const authLiters = this.convertStringNumber(this.data.supply_authorized_liters);
          if (valLiters > authLiters) {
            this.snack.show = true;
            this.snack.message = `LITROS DE ${valLiters.toFixed(3)} L ULTRAPASSA O SALDO DISPONÍVEL DO CARTÃO (${authLiters.toFixed(3)} L)!`;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
            return;
          }
        } else {
          const val = this.convertStringNumber(this.data.supply_value);
          const authVal = this.convertStringNumber(this.data.supply_authorized_value);
          if (val > authVal) {
            this.snack.show = true;
            this.snack.message = `VALOR DE R$ ${val.toFixed(2)} ULTRAPASSA O SALDO DISPONÍVEL DO CARTÃO (R$ ${authVal.toFixed(2)})!`;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
            return;
          }
        }
      }
      
      // Garante que supply_extra está marcado
      this.data.supply_extra = true;
      
      const request = this.data.supply_id
        ? this.$http.put(`/supplies/extra/${this.data.supply_id}`, this.data)
        : this.$http.post(`/supplies/extra`, this.data);
        
      request.then((r) => {
          console.log("Resposta da API (Black Card):", r.data);
          this.snack.show = true;
          if (r.data.error) {
              this.snack.message = `Erro: ${r.data.error}`;
              this.snack.color = "error";
          } else {
              this.snack.message = r.data.result || "Abastecimento concluído!";
              this.snack.color = "success";
          }
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          if (r.data.result) {
            setTimeout(() => {
              this.resetData();
              this.showForm = false;
              this.code = "";
              this.currentBlackCard = null;
              this.initialize();
            }, 1500);
          }
        })
        .catch((e) => {
          console.error("Erro ao concluir:", e);
          this.snack.show = true;
          this.snack.message = e.response?.data?.error || e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    liberarAbastecimento() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      
      const payload = {
        ...this.data,
        supply_status: "EM_ANDAMENTO"
      };

      this.$http
        .put(`/supplies/single/app`, payload)
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.error ? r.data.error : "Abastecimento liberado para o motorista!";
          this.snack.color = `${r.data.error ? "warning" : "success"}`;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          if (r.data.error == null) {
            setTimeout(() => {
              this.showForm = false;
              this.code = '';
              this.initialize();
            }, 1000);
          }
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.response?.data?.error || e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    resetData() {
      this.data = {
        supply_id: null,
        supply_price_day: "0",
        supply_liters_stocked: "0.000",
        supply_authorized_liters: "0.000",
        supply_value: "0.00",
        supply_extra: false,
        supply_bidding_price: "0",
        supply_authorized_value: "0.00",
        supply_adjusted: false,
        supply_billed: false,
        supply_status: "CONCLUIDO",
        item_id: null,
        vehicle_id: null,
        provider_id: null,
        prefecture_id: null,
        is_autorized: false,
        driver_id: null,
        card_id: null,
        supply_extra_type: null,
      };
      this.cardLimitType = "VALUE";
      this.message = null;
    },

    async openFormForBlackCard(card) {
      this.data.is_autorized = true;
      this.data.supply_extra = true;
      this.data.prefecture_id = card.prefecture_id;
      this.data.card_id = card.card_id;
      
      this.$http.get(`/drivers/prefectures/${card.prefecture_id}`).then(d => {
        this.drivers = d.data;
        const driver = d.data.find(f => f.driver_name.toUpperCase() === (card.client_name || '').toUpperCase());
        if (driver) this.data.driver_id = driver.driver_id;
      });

      const attendant = await this.$http.get(`/attendants/${this.$store.getters.getUserData.table_id}`);
      const providerId = attendant.data[0].provider_id;
      this.data.provider_id = providerId;
      
      const itemsRes = await this.$http.get(`/supplies/providers/prefectures/${providerId}/${card.prefecture_id}`);
      this.items = itemsRes.data;
      
      if (this.items.length === 1) {
        this.data.item_id = this.items[0].item_id;
        this.loadBidding(this.items[0].item_id);
      }
      
      this.cardLimitType = card.card_limit_type || "VALUE";
      if (this.cardLimitType === 'LITERS') {
        const remaining = Number(card.card_limit_liters) - Number(card.card_used_liters || 0);
        this.message = `PORTADOR AUTENTICADO: ${card.client_name}. SALDO DISPONÍVEL NO CARTÃO: ${this.realMaskII(this.safeToFixed(remaining, 3))} LITROS`;
        this.data.supply_authorized_liters = this.safeToFixed(remaining, 3);
        this.data.supply_authorized_value = 0;
      } else {
        const remaining = Number(card.card_limit) - Number(card.card_used_limit || 0);
        this.message = `PORTADOR AUTENTICADO: ${card.client_name}. SALDO DISPONÍVEL NO CARTÃO: R$ ${this.realMask(this.safeToFixed(remaining, 2))}`;
        this.data.supply_authorized_value = this.safeToFixed(remaining, 2);
        this.data.supply_authorized_liters = 0;
      }
      
      this.showForm = true;
      this.loader = false;
    },

    cancelAuthWaiting() {
      if (this.authCheckInterval) clearInterval(this.authCheckInterval);
      if (this.currentBlackCard) {
        this.$http.post("/supplies/black-card/cancel-request", {
          card_id: this.currentBlackCard.card_id,
        }).catch(() => {});
      }
      this.isWaitingUserAppAuth = false;
      this.pendingAuthRequest = null;
      this.currentBlackCard = null;
    },
  },
  beforeDestroy() {
    if (this.authCheckInterval) clearInterval(this.authCheckInterval);
  },
};
</script>

<style>
.title {
  text-transform: uppercase;
  font-weight: 400;
}

.flexd {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}
.flexd p {
  margin: 10px 0;
  font-weight: bold;
  text-transform: uppercase;
}

.fade-enter-active {
  transition: opacity 1s;
}
.fade-enter {
  opacity: 0;
}
</style>
